import {
  disclosure,
  disclosureCss,
  kitPartCss,
  panel,
  panelAction,
  panelCss,
  rowList,
  rowsCss,
} from '../armKit';
import { uploadDocumentUrl, workEditUrl } from '../destinations';
import { EDGE, INK, MUTED } from '../surface';

import type { Row } from '../armKit';
import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';

type Facts = Map<number, WorkFacts>;
type Gap = { workId: number; title: string };

const EXPAND_SOURCE = 'profile-liautotest-console-expand';
const FIX_SOURCE = 'profile-liautotest-console-fix';
const UPLOAD_SOURCE = 'profile-liautotest-console-upload';
const UPLOAD_LABEL = 'Add a paper';
const UPLOAD_ENTRY = 'profile';
// workFacts stops at the first 100 works, so a longer corpus is only sampled.
const FACTS_LIMIT = 100;
const LEAD_GAPS = 3;

const HEAD_CLASS = 'profile-liautotest-console__head';
const ICON_CLASS = 'profile-liautotest-console__icon';
const TITLE_CLASS = 'profile-liautotest-console__title';
const NOTE_CLASS = 'profile-liautotest-console__note';

const CALLOUT_BLUE = '#0645b1';
const CALLOUT_TINT = '#ffffff';

function armCss(scope: string): string {
  return (
    panelCss(scope, { frame: 'card', padding: '16px', marginBottom: '0' }) +
    kitPartCss(
      'panel',
      scope,
      `background: ${CALLOUT_TINT}; border-color: ${CALLOUT_BLUE}; ` +
        'border-radius: 7px;',
    ) +
    `${scope} .${HEAD_CLASS} {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
${scope} .${ICON_CLASS} {
  flex: 0 0 auto;
  font-size: 20px;
  margin-top: 1px;
  color: ${CALLOUT_BLUE};
}
${scope} .${TITLE_CLASS} {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
  color: ${INK};
  margin: 0;
}
${scope} .${NOTE_CLASS} {
  font-size: 13px;
  line-height: 1.4;
  color: ${MUTED};
  margin: 4px 0 0;
}
` +
    rowsCss(scope, { marginTop: '14px', itemPadding: '8px 0' }) +
    kitPartCss('row', scope, `border-top-color: ${EDGE};`) +
    disclosureCss(scope) +
    kitPartCss('summary', scope, 'margin-top: 10px; font-weight: 600;') +
    kitPartCss(
      'action',
      scope,
      `display: block; margin-top: 14px; padding-top: 14px; ` +
        `border-top: 1px solid ${EDGE}; font-size: 14px; font-weight: 600;`,
    )
  );
}

function gapsIn(facts: Facts, ctx: ArmContext): Gap[] {
  const rendered = new Map(ctx.cards().map((card) => [card.workId, card]));

  return Array.from(facts.entries()).flatMap(([workId, fact]) => {
    if (fact.hasFile !== false) return [];
    // The card itself offers a download — a "no file" row for it would
    // contradict what the page next to it already shows.
    if (rendered.get(workId)?.hasDownloadable === true) return [];

    const title = fact.title?.trim() ?? '';

    return title === '' ? [] : [{ workId, title }];
  });
}

function titleFor(gapCount: number, sampled: boolean): string {
  const subject = sampled
    ? `${gapCount} of your first ${FACTS_LIMIT} papers`
    : `${gapCount} ${gapCount === 1 ? 'paper' : 'papers'}`;

  return `${subject} ${gapCount === 1 ? 'has' : 'have'} no file to download`;
}

function noteFor(gapCount: number): string {
  const object = gapCount === 1 ? 'it' : 'these';

  return `Readers can't download ${object} until you add a file.`;
}

function infoIcon(): HTMLElement {
  const icon = document.createElement('span');
  icon.className = `${ICON_CLASS} material-symbols-outlined`;
  icon.textContent = 'info';
  icon.setAttribute('translate', 'no');
  icon.setAttribute('aria-hidden', 'true');

  return icon;
}

function headFor(gapCount: number, sampled: boolean): HTMLElement {
  const head = document.createElement('div');
  head.className = HEAD_CLASS;
  head.appendChild(infoIcon());

  const copy = document.createElement('div');

  const title = document.createElement('p');
  title.className = TITLE_CLASS;
  title.textContent = titleFor(gapCount, sampled);
  copy.appendChild(title);

  const note = document.createElement('p');
  note.className = NOTE_CLASS;
  note.textContent = noteFor(gapCount);
  copy.appendChild(note);

  head.appendChild(copy);

  return head;
}

function rowFor(gap: Gap, ctx: ArmContext): Row {
  return {
    title: gap.title,
    url: workEditUrl(gap.workId),
    onClick: () => ctx.track(FIX_SOURCE, { entityId: gap.workId }),
  };
}

function consoleFor(
  gaps: Gap[],
  sampled: boolean,
  ctx: ArmContext,
): HTMLElement {
  const { root, body } = panel();

  body.appendChild(headFor(gaps.length, sampled));

  const rows = gaps.map((gap) => rowFor(gap, ctx));
  body.appendChild(rowList(rows.slice(0, LEAD_GAPS)));

  const rest = rows.slice(LEAD_GAPS);
  if (rest.length > 0) {
    body.appendChild(
      disclosure(`View ${rest.length} more`, rowList(rest), () =>
        ctx.track(EXPAND_SOURCE),
      ),
    );
  }

  body.appendChild(
    panelAction(UPLOAD_LABEL, uploadDocumentUrl(UPLOAD_ENTRY), () =>
      ctx.track(UPLOAD_SOURCE),
    ),
  );

  return root;
}

export const ownerUploadConsole: ArmSpec = {
  onReady: async (ctx) => {
    const facts = await ctx.workFacts();

    const gaps = gapsIn(facts, ctx);
    if (gaps.length === 0) return;

    const slot = ctx.mountSlot('columnTop');
    if (slot == null) return;

    ctx.injectStyles(armCss(ctx.scope));
    slot.appendChild(consoleFor(gaps, facts.size >= FACTS_LIMIT, ctx));
  },
};
